
import React from 'react';
import { Project, ProjectStage } from '../types';
import { STAGE_CONFIG, PRIORITY_CONFIG } from '../constants';
import { Calendar, Users, AlertCircle, Clock } from 'lucide-react';
// Fix: remove parseISO as it is not available from the installed date-fns version
import { differenceInDays, format } from 'date-fns';

interface ProjectCardProps {
  project: Project;
  onClick: () => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onClick }) => {
  // Logic to determine which deadline to show
  const getDisplayDeadline = () => {
    if (project.stage === ProjectStage.DESIGN && project.designDeadline) return project.designDeadline;
    if (project.stage === ProjectStage.DEVELOPMENT && project.devDeadline) return project.devDeadline;
    return project.currentDeadline || project.overallDeadline;
  };

  const deadlineStr = getDisplayDeadline();
  // Fix: Replace parseISO with native Date constructor
  const deadlineDate = new Date(deadlineStr);
  const today = new Date();
  const daysLeft = differenceInDays(deadlineDate, today);

  const getDeadlineStyles = () => {
    if (daysLeft < 0) return 'text-red-600 bg-red-50 border-red-100';
    if (daysLeft <= 2) return 'text-orange-600 bg-orange-50 border-orange-100';
    return 'text-emerald-600 bg-emerald-50 border-emerald-100';
  };

  const progress = project.stage === ProjectStage.UPCOMING ? 0 :
                   project.stage === ProjectStage.DESIGN ? 20 :
                   project.stage === ProjectStage.DEVELOPMENT ? 40 :
                   project.stage === ProjectStage.QA ? 60 :
                   (project.stage === ProjectStage.SEND_TO_CLIENT || project.stage === ProjectStage.SENT_TO_CLIENT) ? 80 : 100;

  return (
    <div 
      onClick={onClick}
      className="group bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-xl hover:shadow-slate-200/50 hover:border-indigo-300 transition-all cursor-pointer relative overflow-hidden"
    >
      <div className="flex justify-between items-start mb-4">
        <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${PRIORITY_CONFIG[project.priority].color}`}>
          {PRIORITY_CONFIG[project.priority].label}
        </span>
        <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${STAGE_CONFIG[project.stage].color}`}>
          {STAGE_CONFIG[project.stage].icon}
          {STAGE_CONFIG[project.stage].label}
        </div>
      </div>

      <h3 className="font-bold text-slate-900 text-lg mb-1 group-hover:text-indigo-600 transition-colors">{project.name}</h3>
      <p className="text-sm text-slate-500 mb-4 font-medium">{project.clientName}</p>

      <div className="space-y-3 mb-5">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-slate-500">
            <Clock className="w-4 h-4" />
            <span className="text-[10px] font-black uppercase tracking-tight">
              {project.stage === ProjectStage.DESIGN ? 'Design Target' : 
               project.stage === ProjectStage.DEVELOPMENT ? 'Dev Target' : 'Final Deadline'}
            </span>
          </div>
          <span className={`font-bold px-2 py-0.5 rounded border text-[11px] ${getDeadlineStyles()}`}>
            {format(deadlineDate, 'MMM d, yyyy')}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-slate-500">
            <Users className="w-4 h-4" />
            <span className="text-[10px] font-black uppercase tracking-tight">Personnel</span>
          </div>
          <div className="flex -space-x-2">
            {[project.assignedDesignerId, project.assignedDevManagerId, project.assignedQAId].filter(Boolean).map((id, i) => (
              <img key={id} src={`https://picsum.photos/seed/${id}/40/40`} className="w-7 h-7 rounded-full border-2 border-white" alt="Team member" />
            ))}
          </div>
        </div>
      </div>

      <div className="relative pt-1">
        <div className="flex mb-2 items-center justify-between text-xs">
          <span className="font-bold text-indigo-600 uppercase tracking-wider">Overall Progress</span>
          <span className="font-bold text-slate-900">{progress}%</span>
        </div>
        <div className="overflow-hidden h-2 mb-1 text-xs flex rounded bg-slate-100">
          <div 
            style={{ width: `${progress}%` }} 
            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500 transition-all duration-500"
          ></div>
        </div>
      </div>

      {project.isDelayed && (
        <div className="absolute top-0 right-0 bg-red-600 text-white px-8 py-1 rotate-45 translate-x-1/2 -translate-y-1/2 shadow-lg">
          <span className="text-[10px] font-bold tracking-widest flex items-center gap-1 ml-4">
            <AlertCircle className="w-3 h-3" /> DELAYED
          </span>
        </div>
      )}
    </div>
  );
};
